/**
 * DefiDisclaimer — one-time-per-session DeFi risk acknowledgment dialog.
 *
 * Shows risk warnings; user must check "I understand" + click "Continue" to proceed.
 * Stores acknowledgment in sessionStorage (clears on tab close).
 *
 * Story 5.4 Task 2
 */

import { type ReactNode, useState } from "react";

import { Modal } from "../ui/Modal";

function WarningIcon(): ReactNode {
	return (
		<svg
			className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0"
			viewBox="0 0 20 20"
			fill="currentColor"
			aria-hidden="true"
		>
			<path
				fillRule="evenodd"
				d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
				clipRule="evenodd"
			/>
		</svg>
	);
}

const DISCLAIMER_KEY = "defi-disclaimer-ack";

/** Check if the DeFi disclaimer has been acknowledged this session. */
export function isDisclaimerAcknowledged(): boolean {
	return typeof window !== "undefined" && sessionStorage.getItem(DISCLAIMER_KEY) === "true";
}

type DefiDisclaimerProps = {
	open: boolean;
	onAcknowledge: () => void;
	onCancel: () => void;
};

export function DefiDisclaimer({ open, onAcknowledge, onCancel }: DefiDisclaimerProps) {
	const [checked, setChecked] = useState(false);

	function handleCancel() {
		setChecked(false);
		onCancel();
	}

	function handleContinue() {
		sessionStorage.setItem(DISCLAIMER_KEY, "true");
		setChecked(false);
		onAcknowledge();
	}

	return (
		<Modal isOpen={open} onClose={handleCancel} title="DeFi Risk Disclaimer">
			<div className="space-y-4">
				<ul
					className="space-y-3 text-sm text-pastel-gray"
					id="risk-warnings-list"
					data-testid="risk-warnings"
				>
					<li className="flex items-start gap-2">
						<WarningIcon />
						<span>
							<strong className="text-pastel-black">Smart contract risk:</strong> funds interact
							with audited but uninsured smart contracts
						</span>
					</li>
					<li className="flex items-start gap-2">
						<WarningIcon />
						<span>
							<strong className="text-pastel-black">Price volatility:</strong> token prices can
							change rapidly and significantly
						</span>
					</li>
					<li className="flex items-start gap-2">
						<WarningIcon />
						<span>
							<strong className="text-pastel-black">Slippage risk:</strong> you may receive less
							than the quoted amount
						</span>
					</li>
					<li className="flex items-start gap-2">
						<WarningIcon />
						<span>
							<strong className="text-pastel-black">Not financial advice:</strong> do your own
							research before trading
						</span>
					</li>
				</ul>

				<label className="flex items-center gap-2 cursor-pointer">
					<input
						type="checkbox"
						checked={checked}
						onChange={(e) => setChecked(e.target.checked)}
						className="h-4 w-4 rounded border-pastel-border text-pastel-vibrant-blue focus:ring-pastel-vibrant-blue"
						aria-describedby="risk-warnings-list"
						data-testid="disclaimer-checkbox"
					/>
					<span className="text-sm font-medium text-pastel-black">I understand the risks</span>
				</label>

				<div className="flex gap-3">
					<button
						type="button"
						onClick={handleCancel}
						className="flex-1 rounded-lg border border-pastel-border px-4 py-2 text-sm font-medium text-pastel-gray hover:bg-pastel-bg transition-colors"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleContinue}
						disabled={!checked}
						className="flex-1 rounded-lg bg-pastel-vibrant-blue px-4 py-2 text-sm font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Continue
					</button>
				</div>
			</div>
		</Modal>
	);
}
